import { useTranslation } from 'react-i18next';
import type { ClientesPorStatus } from '@/types/dashboard';

interface GraficoClientesPorStatusProps {
  dados: ClientesPorStatus[];
}

/**
 * Lista de status com barras horizontais proporcionais. Totalmente
 * monocromática (ink/cinzas) e alinhada com o restante DS — sem dependência
 * de Recharts.
 */
export function GraficoClientesPorStatus({ dados }: GraficoClientesPorStatusProps) {
  const { t } = useTranslation();
  const total = dados.reduce((acc, item) => acc + item.quantidade, 0);

  if (total === 0) {
    return (
      <div className="h-[200px] w-full flex items-center justify-center text-muted-foreground text-sm">
        {t('dashboard.charts.noClientsRegistered')}
      </div>
    );
  }

  // Mostrar todos os status com quantidade > 0, ordenados desc pela contagem.
  const linhas = dados
    .filter((d) => d.quantidade > 0)
    .map((d) => ({
      ...d,
      pct: (d.quantidade / total) * 100,
    }))
    .sort((a, b) => b.quantidade - a.quantidade);

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span>{t('dashboard.charts.clientsByStatus')}</span>
        <span className="tabular-nums font-mono">
          {total} {t('dashboard.charts.clientsLabel')}
        </span>
      </div>

      <ul className="flex flex-col gap-2.5">
        {linhas.map((linha) => (
          <li key={linha.status} className="flex flex-col gap-1">
            <div className="flex items-center justify-between text-[13px]">
              <span className="text-foreground font-medium truncate">{linha.status}</span>
              <span className="text-muted-foreground tabular-nums font-mono text-xs">
                {linha.quantidade} · {linha.pct.toFixed(0)}%
              </span>
            </div>
            <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
              <div
                className="h-full bg-foreground rounded-full transition-[width] duration-300"
                style={{ width: `${linha.pct}%` }}
                aria-label={`${linha.pct.toFixed(0)}%`}
              />
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
